import React from 'react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { Text } from '../../../components/ui';

interface ResultsGraphProps {
  distributionData: Array<{
    x: number;
    'Saw 10': number;
    'Saw 65': number;
  }>;
  userEstimate?: string;
}

// Custom tooltip component for cleaner presentation
const CustomTooltip = ({ active, payload, label }: any) => {
  // Only show tooltip on larger screens
  if (window.innerWidth < 640 || !active || !payload || payload.length === 0) return null;
  
  return (
    <div className="bg-white p-3 rounded-lg border border-gray-200 shadow-sm text-sm">
      <p className="font-medium mb-1">{label}% estimate</p>
      {payload.map((entry: any, index: number) => (
        <p key={index} style={{ color: entry.color }}>
          {entry.name.startsWith('Saw') ? `Group that saw ${entry.name.split(' ')[1]}` : entry.name}
        </p>
      ))}
    </div>
  );
};

export const ResultsGraph: React.FC<ResultsGraphProps> = ({ distributionData, userEstimate }) => {
  return (
    <div className="flex flex-col h-full w-full">      
      {/* Chart area - give it most of the space */}
      <div className="flex-1" style={{ height: "80%" }}>
        <ResponsiveContainer width="100%" height="100%" minWidth={360}>
          <LineChart 
            data={distributionData} 
            margin={{ 
              top: 20,
              right: 30,
              left: 10,
              bottom: 30
            }}
          >
            <XAxis 
              dataKey="x"
              label={{ 
                value: 'Estimated Percentage', 
                position: 'bottom', 
                offset: 15,
                style: {
                  textAnchor: 'middle',
                  fontSize: '0.875rem',
                  fill: '#4B5563'
                }
              }}
              domain={[0, 100]}
              tick={{ fill: '#4B5563' }}
              ticks={[0, 20, 40, 60, 80, 100]}
            />
            <YAxis 
              label={{ 
                value: 'Distribution', 
                angle: -90, 
                position: 'insideLeft',
                offset: 0,
                style: {
                  textAnchor: 'middle',
                  fontSize: '0.875rem',
                  fill: '#4B5563'
                }
              }} 
              tick={false}
              axisLine={true}
            />
            {/* Tooltip will be hidden on mobile screens */}
            <Tooltip content={<CustomTooltip />} />
            <ReferenceLine x={10} stroke="#9CA3AF" strokeDasharray="3 3" />
            <ReferenceLine x={65} stroke="#9CA3AF" strokeDasharray="3 3" />
            {userEstimate && (
              <ReferenceLine 
                x={Number(userEstimate)} 
                stroke="#059669" 
                strokeWidth={2}
              />
            )}
            <Line 
              type="monotone" 
              dataKey="Saw 10" 
              stroke="#6366f1" 
              strokeWidth={3} 
              dot={false}
              name="Saw 10"
            />
            <Line 
              type="monotone" 
              dataKey="Saw 65" 
              stroke="#f97316" 
              strokeWidth={3} 
              dot={false}
              name="Saw 65"
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Legend area - with more space allocated for mobile */}
      <div className="mt-1" style={{ height: "20%" }}>
        <div className="flex flex-row justify-center items-center gap-4 sm:gap-8">
          {['Saw 10', 'Saw 65'].map((key) => (
            <div key={key} className="flex items-center gap-2">
              <div 
                className="w-3 h-3 sm:w-4 sm:h-4 rounded-full" 
                style={{ backgroundColor: key === 'Saw 10' ? '#6366f1' : '#f97316' }}
              />
              <span className="text-xs sm:text-sm text-gray-700">
                Group shown {key.split(' ')[1]}
              </span>
            </div>
          ))}
          {userEstimate && (
            <div className="flex items-center gap-2">
              <div className="w-6 sm:w-8 h-0.5 bg-emerald-600" />
              <Text className="text-xs sm:text-sm text-emerald-600">Your guess: {userEstimate}%</Text>
            </div>
          )}
        </div>
        
        <Text className="text-xs text-gray-500 text-center italic mt-1 sm:mt-2">
          Note: Distribution curves approximate the study results
        </Text>
      </div>
    </div>
  );
}; 